import { computed, Directive, effect, ElementRef, HostBinding, HostListener, inject, input } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';
import { ucumToDateFromDate } from 'shared/util-common';

import { Ucum } from '@treetop/shared/model';

import { DatepickerControl } from './datepicker-panel.directive';
import { CustomDateAdapter, DatepickerComponent } from './datepicker.component';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';

@Directive({
  selector: 'input[ttDatepicker]',
  standalone: true,
  providers: [
    { provide: FormFieldControl, useExisting: DatepickerInputDirective },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
  ],
})
export class DatepickerInputDirective implements FormFieldControl, DatepickerControl, ControlValueAccessor {
  protected _elementRef = inject(ElementRef);

  public ngControl = inject(NgControl, { optional: true, self: true });
  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentFormGroup = inject(FormGroupDirective, { optional: true });
  private parentForm = inject(NgForm, { optional: true });
  private _dateAdapter = inject(DateAdapter);
  private translocoService = inject(TranslocoService);
  private datePicker: DatepickerComponent;

  errorState = computed(() => this.errorStateTracker.errorState());
  private errorStateTracker: ErrorStateTracker;
  private disabled = false;

  public getCurrentValue(): Date | Ucum {
    return this.ngControl?.control?.value;
  }

  /**
   * Whether the calculated date should be shown in the input field if an UCUM value is selected
   */
  public showCalculatedDate = input<boolean>(false);

  public ttDatepicker = input<DatepickerComponent>();

  @HostBinding('class.tt-datepicker-input') ttDatepickerInput = true;

  @HostListener('input', ['$event.target.value']) onInput(value: string): void {
    this._onChange(value);
  }

  @HostBinding('class.tt-datepicker-input--disabled') get isDisabledClass(): boolean {
    return this.disabled;
  }

  @HostListener('click') handleClick(): void {
    this.datePicker?.open();
  }

  constructor() {
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      const ttDatepicker = this.ttDatepicker();
      if (ttDatepicker) {
        this.registerInput(ttDatepicker);
      }
    });

    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);
  }

  /** Gets the element to which the calendar overlay should be attached. */
  public getConnectedOverlayOrigin(): ElementRef {
    return this._elementRef;
  }

  private registerInput(datepicker: DatepickerComponent): void {
    datepicker.registerInput(this);
    datepicker.selectedDateChange.subscribe((date) => {
      this._updateInputValue(date);
    });

    this.datePicker = datepicker;
  }

  private _updateInputValue(value: Date | Ucum): void {
    if (this.ngControl?.control) {
      this.ngControl.control.setValue(value);
    } else {
      this._elementRef.nativeElement.value = value;
    }
  }

  public isDisabled(): boolean {
    return this.disabled;
  }

  // ------------- ControlValueAccessor related methods -------------

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange: (value: unknown) => void = () => {};

  writeValue(value: Date | Ucum): void {
    if (!value) {
      this._elementRef.nativeElement.value = '';
      return;
    }

    if (value instanceof Date) {
      this._elementRef.nativeElement.value = this._dateAdapter.format(value, undefined);
    } else if (typeof value === 'string') {
      const months = parseInt(value.match(/\d+/)[0], 10);
      const date = ucumToDateFromDate(value);
      const formatedValue = this._dateAdapter.format(date, undefined);

      this._elementRef.nativeElement.value = `${this.translocoService.translate('Datepicker.InMonths', { months })} ${this.showCalculatedDate() ? '(' + formatedValue + ')' : ''}`;
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
